import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare } from "lucide-react";

interface SMSPreferencesSectionProps {
  userType: "buyer" | "seller";
  initialSmsOptIn: boolean;
  userId?: string;
  buyerProfileId?: string;
}

export const SMSPreferencesSection = ({
  userType,
  initialSmsOptIn,
  userId,
  buyerProfileId,
}: SMSPreferencesSectionProps) => {
  const [smsOptIn, setSmsOptIn] = useState(initialSmsOptIn);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async (checked: boolean) => {
    setIsLoading(true);
    
    try {
      if (userType === "buyer" && buyerProfileId) {
        const { error } = await supabase
          .from("buyer_profiles")
          .update({ sms_opt_in: checked } as any)
          .eq("id", buyerProfileId);

        if (error) throw error;
      } else if (userType === "seller" && userId) {
        const { error } = await supabase
          .from("profiles")
          .update({ sms_opt_in: checked } as any)
          .eq("id", userId);

        if (error) throw error;
      }

      setSmsOptIn(checked);
      
      toast({
        title: "SMS Preferences Updated",
        description: checked 
          ? "You will now receive SMS notifications about new properties and updates." 
          : "SMS notifications have been disabled. You can re-enable them anytime.",
      });
    } catch (error) {
      console.error("Error updating SMS preferences:", error);
      toast({
        title: "Update Failed",
        description: "There was a problem updating your SMS preferences.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle>SMS Notifications</CardTitle>
            <CardDescription className="mt-1.5">
              Manage your text message notification preferences
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div className="flex-1 space-y-1">
            <Label htmlFor="sms-opt-in" className="text-base font-medium cursor-pointer">
              Receive SMS Notifications
            </Label>
            <p className="text-sm text-muted-foreground">
              Get text alerts about new properties, updates, and important account notifications.
            </p>
          </div>
          <Switch
            id="sms-opt-in"
            checked={smsOptIn}
            onCheckedChange={handleToggle}
            disabled={isLoading}
            className="mt-1"
          />
        </div>

        <div className="rounded-lg bg-muted/50 p-4 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              TCPA Compliance Notice
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              By enabling SMS notifications, you consent to receive automated text messages from GoDyspo 
              at the phone number provided. Message and data rates may apply. You can opt out at any time 
              by replying STOP or disabling this setting. Standard terms and conditions apply.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
